/*eslint-disable*/
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReactFlow, {
  Background,
  BackgroundVariant,
  Connection,
  Controls,
  Edge,
  EdgeChange,
  Node,
  NodeChange,
  ReactFlowInstance,
  ReactFlowProvider,
  applyEdgeChanges,
  applyNodeChanges,
} from 'reactflow'
import 'reactflow/dist/style.css'
import { useDispatch, useSelector } from 'react-redux'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'

import { Animation, Button, Flex, TabFooter } from 'styling/GlobalStyles'
import { CancelBtn } from 'components/Button/CancelBtn'
import { ClearButton } from 'components/Button/ClearButton'
import { ItemsPick } from 'components/ItemsPick/ItemsPick'
import { DropDownTabsContainer } from 'components/DropDownTabsContainer/DropDownTabsContainer'
import { AppDispatch, RootState } from 'store'
import { getTasksTemplateLogics } from 'features/taskTemplates/taskTemplatesSlice'
import { IGetAllLogicsSingleResponse } from 'types/api/tasksTemplatesApi.interface'
import {
  IWorkingFunctions,
  ITaskTemplateStateComponent,
  ITaskTemplatesTab,
} from 'types/task-templates.interface'

import {
  TaskLogicCanvas,
  TaskLogicEmptyHint,
  TaskLogicToolbar,
} from './DragComponents/Tasks.styled'
import {
  CalcComponentNode,
  CalcNodeData,
  FunctionNode,
  FunctionNodeData,
  InputComponentNode,
  InputNodeData,
} from './Components/TaskTLogicNodes'
import { TaskLogicEdge } from './Components/TaskTLogicEdge'

const nodeTypes = {
  inputComp: InputComponentNode,
  calcComp: CalcComponentNode,
  func: FunctionNode,
}

const edgeTypes = { taskLogic: TaskLogicEdge }

const COLUMN_X = { input: 0, func: 360, calc: 760 }
const ROW_GAP = 130

interface ITaskTLogicTab extends ITaskTemplatesTab {
  handleLogicSubmit: (functionType: number) => void
}

const TaskTLogicTabInner = ({ state, setState, handleLogicSubmit }: ITaskTLogicTab) => {
  const dispatch = useDispatch<AppDispatch>()
  const tasksLogics = useSelector(
    (s: RootState) => s.taskTemplates.tasksLogics,
  ) as unknown as IGetAllLogicsSingleResponse[]
  const [tab, setTab] = useState(
    !state.functionType || state.functionType == 1 ? 'Internal functions' : 'AI functions',
  )
  const [addedButNotConnected, setAddedButNotConnected] = useState<IWorkingFunctions[]>([])
  const flowRef = useRef<ReactFlowInstance | null>(null)
  const [nodes, setNodes] = useState<Node[]>([])
  const [edges, setEdges] = useState<Edge[]>([])
  const initialized = useRef(false)

  const components: ITaskTemplateStateComponent[] = state.components || []
  const inputs = useMemo(() => components.filter((c) => c.componentType == 1), [components])
  const outputs = useMemo(() => components.filter((c) => c.componentType == 2), [components])

  useEffect(() => {
    dispatch(getTasksTemplateLogics(tab !== 'Internal functions'))
  }, [tab, dispatch])

  const wiredFunctions = useMemo<IWorkingFunctions[]>(() => {
    if (!tasksLogics.length) return []
    const result: IWorkingFunctions[] = []
    for (const c of components.filter((o) => o.componentType == 2 && o.functionId)) {
      const fn = tasksLogics.find((f: IGetAllLogicsSingleResponse) => f.fid == c.functionId)
      if (!fn) continue
      result.push({
        ...fn,
        input: fn.inputs as any,
        notAdded: true,
      })
    }
    return result
  }, [tasksLogics, components])

  const displayFunctions = useMemo<IWorkingFunctions[]>(() => {
    const wiredIds = new Set(wiredFunctions.map((f) => f.fid))
    const extra = addedButNotConnected.filter((f) => !wiredIds.has(f.fid))
    return [...wiredFunctions, ...extra]
  }, [wiredFunctions, addedButNotConnected])

  const positionsRef = useRef<Record<string, { x: number; y: number }>>({})
  const edgesRef = useRef<Edge[]>([])
  const handleEdgeRemovalRef = useRef<(e: Edge) => void>(() => {})

  const handleEdgeRemoveById = useCallback((edgeId: string) => {
    const edge = edgesRef.current.find((e) => e.id === edgeId)
    if (edge) handleEdgeRemovalRef.current(edge)
  }, [])

  const removeFunctionFromList = useCallback((fid: number) => {
    setAddedButNotConnected((prev) => prev.filter((f) => f.fid !== fid))
    setState((prev) => {
      const copy = structuredClone(prev)
      const comp = copy.components.find((c: ITaskTemplateStateComponent) => c.functionId === fid)
      if (comp) {
        comp.functionId = null as any
        comp.functionResultIndex = null as any
        comp.inputParameters = null as any
      }
      return copy
    })
  }, [setState])

  useEffect(() => {
    const next: Node[] = []
    inputs.forEach((inp, i) => {
      const id = `input-${inp.id}`
      const pos = positionsRef.current[id] || { x: COLUMN_X.input, y: i * ROW_GAP }
      positionsRef.current[id] = pos
      next.push({
        id,
        type: 'inputComp',
        position: pos,
        data: { name: inp.name, component: inp.component, componentId: inp.id } as InputNodeData,
      })
    })

    displayFunctions.forEach((fn, i) => {
      const id = `function-${fn.fid}`
      const pos = positionsRef.current[id] || { x: COLUMN_X.func, y: i * (ROW_GAP + 40) }
      positionsRef.current[id] = pos
      const wired = components.some((c) => c.functionId === fn.fid)
      next.push({
        id,
        type: 'func',
        position: pos,
        data: {
          name: fn.name,
          fid: fn.fid,
          inputs: (fn.inputs || (fn.input as any) || []) as any,
          outputs: fn.outputs || [],
          connected: wired,
          onRemove: removeFunctionFromList,
        } as FunctionNodeData,
      })
    })

    outputs.forEach((out, i) => {
      const id = `calc-${out.id}`
      const pos = positionsRef.current[id] || { x: COLUMN_X.calc, y: i * ROW_GAP }
      positionsRef.current[id] = pos
      next.push({
        id,
        type: 'calcComp',
        position: pos,
        data: {
          name: out.name,
          component: out.component,
          componentId: out.id,
          wired: Boolean(out.functionId),
        } as CalcNodeData,
      })
    })

    setNodes(next)
    if (!initialized.current && next.length > 0) {
      initialized.current = true
      setTimeout(() => flowRef.current?.fitView({ padding: 0.2 }), 50)
    }
  }, [inputs, outputs, displayFunctions, components, removeFunctionFromList])

  useEffect(() => {
    const next: Edge[] = []

    for (const c of components.filter((c) => c.componentType == 2 && c.functionId)) {
      const fn = tasksLogics.find((f: IGetAllLogicsSingleResponse) => f.fid === c.functionId)
      if (!fn) continue
      const output = fn.outputs.find((o) => o.order === c.functionResultIndex)
      if (!output) continue
      next.push({
        id: `e-fout-${fn.fid}-${output.component}-calc-${c.id}`,
        source: `function-${fn.fid}`,
        sourceHandle: `fout-${output.component}`,
        target: `calc-${c.id}`,
        targetHandle: 'in',
        type: 'taskLogic',
        data: { variant: 'output', onRemove: handleEdgeRemoveById },
      })
      for (const ip of c.inputParameters || []) {
        if (typeof ip.value !== 'number') continue
        const inputComp = components.find((cnp) => cnp.id === ip.value && cnp.componentType === 1)
        if (!inputComp) continue
        next.push({
          id: `e-input-${inputComp.id}-fin-${fn.fid}-${inputComp.component}`,
          source: `input-${inputComp.id}`,
          sourceHandle: 'out',
          target: `function-${fn.fid}`,
          targetHandle: `fin-${inputComp.component}`,
          type: 'taskLogic',
          data: { variant: 'input', onRemove: handleEdgeRemoveById },
        })
      }
    }

    for (const fn of addedButNotConnected) {
      for (const inp of fn.input || []) {
        if (inp.tempConnectedInput) {
          const inputComp = components.find(
            (c) => c.id === inp.tempConnectedInput!.componentId && c.componentType === 1,
          )
          if (!inputComp) continue
          next.push({
            id: `e-input-${inputComp.id}-fin-${fn.fid}-${inputComp.component}-temp`,
            source: `input-${inputComp.id}`,
            sourceHandle: 'out',
            target: `function-${fn.fid}`,
            targetHandle: `fin-${inputComp.component}`,
            type: 'taskLogic',
            data: { variant: 'input', onRemove: handleEdgeRemoveById },
          })
        }
      }
    }

    edgesRef.current = next
    setEdges(next)
  }, [components, tasksLogics, addedButNotConnected, handleEdgeRemoveById])

  const onNodesChange = useCallback((changes: NodeChange[]) => {
    setNodes((prev) => {
      const updated = applyNodeChanges(changes, prev)
      for (const change of changes) {
        if (change.type === 'position' && change.position) {
          positionsRef.current[change.id] = change.position
        }
      }
      return updated
    })
  }, [])

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => setEdges((es) => applyEdgeChanges(changes, es)),
    [],
  )

  const parseNodeRefs = (
    connection: Connection,
  ): {
    fromInput?: ITaskTemplateStateComponent
    fromFnId?: number
    fromFnOutputComponent?: number
    toFnId?: number
    toFnInputComponent?: number
    toCalc?: ITaskTemplateStateComponent
  } => {
    const out: any = {}
    if (connection.source?.startsWith('input-')) {
      const inputId = Number(connection.source.slice('input-'.length))
      out.fromInput = components.find((c) => c.id === inputId)
    }
    if (connection.source?.startsWith('function-')) {
      out.fromFnId = Number(connection.source.slice('function-'.length))
      if (connection.sourceHandle?.startsWith('fout-')) {
        out.fromFnOutputComponent = Number(connection.sourceHandle.slice('fout-'.length))
      }
    }
    if (connection.target?.startsWith('function-')) {
      out.toFnId = Number(connection.target.slice('function-'.length))
      if (connection.targetHandle?.startsWith('fin-')) {
        out.toFnInputComponent = Number(connection.targetHandle.slice('fin-'.length))
      }
    }
    if (connection.target?.startsWith('calc-')) {
      const calcId = Number(connection.target.slice('calc-'.length))
      out.toCalc = components.find((c) => c.id === calcId)
    }
    return out
  }

  const isValidConnection = useCallback(
    (connection: Connection) => {
      const refs = parseNodeRefs(connection)
      if (refs.fromInput && refs.toFnId != null && refs.toFnInputComponent != null) {
        return refs.fromInput.component === refs.toFnInputComponent
      }
      if (refs.fromFnId != null && refs.fromFnOutputComponent != null && refs.toCalc) {
        return refs.toCalc.component === refs.fromFnOutputComponent && !refs.toCalc.functionId
      }
      return false
    },
    [components],
  )

  const onConnect = useCallback(
    (connection: Connection) => {
      const refs = parseNodeRefs(connection)
      if (!isValidConnection(connection)) return

      if (refs.fromInput && refs.toFnId != null && refs.toFnInputComponent != null) {
        const fnId = refs.toFnId
        const wiredComp = components.find((c) => c.functionId === fnId)
        if (wiredComp) {
          setState((prev) => {
            const copy = structuredClone(prev)
            const compIndex = copy.components.findIndex(
              (c: ITaskTemplateStateComponent) => c.functionId === fnId,
            )
            if (compIndex < 0) return prev
            const target = copy.components[compIndex]
            target.inputParameters = target.inputParameters || []
            const exists = target.inputParameters.some(
              (ip) => typeof ip.value === 'number' && ip.value === refs.fromInput!.id,
            )
            if (!exists) target.inputParameters.push({ type: 1, value: refs.fromInput!.id })
            return copy
          })
        } else {
          setAddedButNotConnected((prev) => {
            const copy = structuredClone(prev)
            const index = copy.findIndex((f) => f.fid === fnId)
            if (index < 0) return prev
            const inputIndex = copy[index].input.findIndex(
              (i) => i.component === refs.toFnInputComponent,
            )
            if (inputIndex < 0) return prev
            copy[index].input[inputIndex].tempConnectedInput = { componentId: refs.fromInput!.id }
            return copy
          })
        }
        return
      }

      if (refs.fromFnId != null && refs.fromFnOutputComponent != null && refs.toCalc) {
        const fn = tasksLogics.find(
          (f: IGetAllLogicsSingleResponse) => f.fid === refs.fromFnId,
        )
        const localFn = addedButNotConnected.find((f) => f.fid === refs.fromFnId)
        if (!fn) return
        const output = fn.outputs.find(
          (o: any) => o.component === refs.fromFnOutputComponent,
        )
        if (!output) return
        setState((prev) => {
          const copy = structuredClone(prev)
          const idx = copy.components.findIndex(
            (c: ITaskTemplateStateComponent) => c.id === refs.toCalc!.id,
          )
          if (idx < 0) return prev
          copy.components[idx].functionId = fn.fid
          copy.components[idx].functionResultIndex = output.order
          if (localFn) {
            const filled = localFn.input.filter((i) => i.tempConnectedInput)
            const newParams = filled.map((fc) => ({
              type: 1 as const,
              value: fc.tempConnectedInput!.componentId,
            }))
            copy.components[idx].inputParameters = newParams.length ? newParams : []
          } else {
            copy.components[idx].inputParameters = copy.components[idx].inputParameters || []
          }
          return copy
        })
        setAddedButNotConnected((prev) => prev.filter((f) => f.fid !== refs.fromFnId))
      }
    },
    [components, tasksLogics, addedButNotConnected, isValidConnection, setState],
  )

  const handleEdgeRemoval = useCallback(
    (e: Edge) => {
      if (e.source.startsWith('input-') && e.target.startsWith('function-')) {
        const inputId = Number(e.source.slice('input-'.length))
        const fnId = Number(e.target.slice('function-'.length))
        const wired = components.find((c) => c.functionId === fnId)
        if (wired) {
          setState((prev) => {
            const copy = structuredClone(prev)
            const comp = copy.components.find(
              (c: ITaskTemplateStateComponent) => c.functionId === fnId,
            )
            if (comp && comp.inputParameters) {
              comp.inputParameters = comp.inputParameters.filter(
                (ip) => !(typeof ip.value === 'number' && ip.value === inputId),
              )
            }
            return copy
          })
        } else {
          setAddedButNotConnected((prev) => {
            const copy = structuredClone(prev)
            const f = copy.find((fn) => fn.fid === fnId)
            if (!f) return prev
            for (const ip of f.input) {
              if (ip.tempConnectedInput?.componentId === inputId) {
                delete ip.tempConnectedInput
              }
            }
            return copy
          })
        }
      } else if (e.source.startsWith('function-') && e.target.startsWith('calc-')) {
        const fnId = Number(e.source.slice('function-'.length))
        const calcId = Number(e.target.slice('calc-'.length))
        const fnDef = tasksLogics.find(
          (f: IGetAllLogicsSingleResponse) => f.fid === fnId,
        )
        let prevParams: any[] = []
        setState((prev) => {
          const copy = structuredClone(prev)
          const idx = copy.components.findIndex(
            (c: ITaskTemplateStateComponent) => c.id === calcId,
          )
          if (idx < 0) return prev
          prevParams = copy.components[idx].inputParameters || []
          copy.components[idx].functionId = null as any
          copy.components[idx].functionResultIndex = null as any
          copy.components[idx].inputParameters = null as any
          return copy
        })
        if (fnDef) {
          setAddedButNotConnected((p) => {
            if (p.some((f) => f.fid === fnId)) return p
            const inputs = (fnDef.inputs || []).map((inp) => {
              const param = prevParams.find(
                (pp) =>
                  typeof pp.value === 'number' &&
                  components.find(
                    (cm) => cm.id === pp.value && cm.component === inp.component,
                  ),
              )
              return {
                component: inp.component,
                name: inp.name,
                order: inp.order,
                ...(param ? { tempConnectedInput: { componentId: param.value as number } } : {}),
              }
            })
            return [
              ...p,
              { ...fnDef, input: inputs as any, notAdded: true as const } as IWorkingFunctions,
            ]
          })
        }
      }
    },
    [components, tasksLogics, setState],
  )

  handleEdgeRemovalRef.current = handleEdgeRemoval

  const onEdgesDelete = useCallback(
    (removed: Edge[]) => removed.forEach(handleEdgeRemoval),
    [handleEdgeRemoval],
  )

  const handleAddFunc = (item: IGetAllLogicsSingleResponse) => {
    if (addedButNotConnected.some((f) => f.fid === item.fid)) return
    if (wiredFunctions.some((f) => f.fid === item.fid)) return
    setAddedButNotConnected((prev) => [
      ...prev,
      { ...item, input: item.inputs as any, notAdded: true as const },
    ])
  }

  const handleClearLogic = () => {
    setAddedButNotConnected([])
    setState((prev) => {
      const copy = structuredClone(prev)
      copy.components = copy.components.map((c: ITaskTemplateStateComponent) => ({
        ...c,
        functionId: null as any,
        functionResultIndex: null as any,
        inputParameters: null as any,
      }))
      return copy
    })
  }

  const handleActive = (newTab: string) => {
    handleClearLogic()
    setTab(newTab)
  }

  const handleFormatLayout = () => {
    positionsRef.current = {}
    initialized.current = false
    setNodes((prev) => {
      const inp = prev.filter((n) => n.type === 'inputComp')
      const fns = prev.filter((n) => n.type === 'func')
      const calc = prev.filter((n) => n.type === 'calcComp')
      const reset = (arr: Node[], x: number, stepY: number) =>
        arr.map((n, i) => {
          const newPos = { x, y: i * stepY }
          positionsRef.current[n.id] = newPos
          return { ...n, position: newPos }
        })
      return [
        ...reset(inp, COLUMN_X.input, ROW_GAP),
        ...reset(fns, COLUMN_X.func, ROW_GAP + 40),
        ...reset(calc, COLUMN_X.calc, ROW_GAP),
      ]
    })
    setTimeout(() => flowRef.current?.fitView({ padding: 0.2, duration: 300 }), 50)
  }

  return (
    <Animation>
      <Flex between gap='1rem' height='80vh'>
        <TaskLogicCanvas>
          <TaskLogicToolbar>
            <Button height='36' width='220' onClick={handleFormatLayout}>
              Format layout
            </Button>
          </TaskLogicToolbar>
          <TaskLogicEmptyHint visible={nodes.length === 0}>
            Add inputs/outputs on Components tab, then drag a function from the right panel
          </TaskLogicEmptyHint>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onEdgesDelete={onEdgesDelete}
            isValidConnection={isValidConnection}
            onInit={(instance) => {
              flowRef.current = instance
              if (nodes.length > 0) instance.fitView({ padding: 0.2 })
            }}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
            minZoom={0.2}
            maxZoom={1.5}
            defaultEdgeOptions={{ type: 'taskLogic' }}
            proOptions={{ hideAttribution: true }}
          >
            <Background variant={BackgroundVariant.Dots} gap={20} size={1} />
            <Controls showInteractive={false} />
          </ReactFlow>
        </TaskLogicCanvas>
        <Flex column gap='1rem' width='30%'>
          <DropDownTabsContainer
            tabs={['Internal functions', 'AI functions']}
            setActive={handleActive}
            active={tab}
          />
          <ItemsPick
            link={tab == 'Internal functions' ? 'task/template/logics' : 'task/template/logics/ai'}
            width='100%'
            selectedList={displayFunctions.map((f) => ({ name: f.name, id: f.fid, fid: f.fid }))}
            oneColumn
            noScroll
            parserFunction={(resp: IGetAllLogicsSingleResponse[]) => resp}
            handleAdd={handleAddFunc}
            placeholder='Search Functions'
            leaveDuplicates
            customSelectedProp='fid'
            resetList
          />
        </Flex>
      </Flex>
      <TabFooter contract>
        <Flex gap='1rem'>
          <ClearButton text='Clear logic' onClick={handleClearLogic} />
          <CancelBtn />
          <Button orange onClick={() => handleLogicSubmit(tab == 'Internal functions' ? 1 : 2)}>
            Save
          </Button>
        </Flex>
      </TabFooter>
    </Animation>
  )
}

export const TaskTLogicTab = (props: ITaskTLogicTab) => {
  return (
    <DndProvider backend={HTML5Backend}>
      <ReactFlowProvider>
        <TaskTLogicTabInner {...props} />
      </ReactFlowProvider>
    </DndProvider>
  )
}
